/**
 * DirectivesModal — UI Controller for the Daily Match Directives modal.
 */

import { directives, DAILY_JACKPOT_FUGAZI } from '../engine/directives.js';
import { wallet } from '../engine/wallet.js';
import { getIconSvg } from './icons.js';

export class DirectivesModal {
  constructor(game = null) {
    this.game = game;
    this.modalEl = document.getElementById('directives-modal');
    this.listEl = document.getElementById('directives-list');
    this.streakEl = document.getElementById('directives-streak');
    this.streakBadgeEl = null;
    this.jackpotBannerEl = document.getElementById('directives-jackpot-banner');
    this.countdownEl = document.getElementById('directives-reset-countdown');
    this.btnBackdrop = document.getElementById('directives-modal-backdrop');
    this.btnReturn = document.getElementById('btn-directives-return');
    this.btnReroll = document.getElementById('btn-daily-reroll');
    this.btnRerollFill = document.getElementById('btn-reroll-fill');
    this.btnRerollText = document.getElementById('btn-reroll-text');

    this.init();
  }

  init() {
    if (this.btnBackdrop) {
      this.btnBackdrop.addEventListener('click', () => {
        this.game?.audio?.playUiClick?.();
        this.close();
      });
    }

    if (this.btnReturn) {
      this.btnReturn.addEventListener('click', () => {
        this.game?.audio?.playUiClick?.();
        this.close();
      });
    }

    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.game?.audio?.playUiClick?.();
          this.close();
          e.stopPropagation();
        }
      });
    }

    if (this.btnReroll) {
      this.btnReroll.addEventListener('click', () => {
        if (directives.canReroll()) {
          directives.rerollDirectives();
          this.render();
        }
      });
    }

    this._initStreakBadge();

    // Reactively update when directives change
    directives.addListener(() => {
      this.updateStreakBadge();
      if (this.isOpen()) {
        this.render();
      }
    });

    // The badge turns red inside the last hours before reset, with no other event to trigger it
    setInterval(() => this.updateStreakBadge(), 30000);

    // Update countdown timer periodically
    setInterval(() => {
      if (this.isOpen()) {
        this.updateCountdown();
      }
    }, 1000);
  }

  open() {
    if (!this.modalEl) return;
    this.game?.pauseForMenu?.('directives');
    // Opened from the Cargo Bay or Game-Over modal: stack above it
    this.modalEl.classList.toggle('over-cargo', !!this.game?.packOpeningModal?.isOpen?.());
    const isGameOver = !!document.getElementById('game-over-modal')?.classList?.contains('active');
    this.modalEl.classList.toggle('over-game-over', isGameOver);
    this.modalEl.classList.remove('hidden');
    this.modalEl.classList.add('active');
    this.render();
  }

  close() {
    if (!this.modalEl) return;
    const wasOpen = this.isOpen();
    if (wasOpen) directives.acknowledgeStreakReward();
    this.modalEl.classList.remove('active');
    this.modalEl.classList.remove('over-cargo');
    this.modalEl.classList.remove('over-game-over');
    this.modalEl.classList.add('hidden');
    if (wasOpen) {
      this.game?.resumeForMenu?.('directives');
    }
  }

  isOpen() {
    return this.modalEl && this.modalEl.classList.contains('active');
  }

  // Flame badge on the "Daily Quests" button: streak length, red pulse when the streak
  // is about to lapse, gold pulse while a freshly claimed milestone is still unseen.
  _initStreakBadge() {
    const btn = document.getElementById('btn-open-directives');
    if (!btn) return;
    this.streakBadgeEl = document.createElement('span');
    this.streakBadgeEl.className = 'streak-badge hidden';
    btn.appendChild(this.streakBadgeEl);
    this.updateStreakBadge();
  }

  updateStreakBadge() {
    const el = this.streakBadgeEl;
    if (!el) return;
    const info = directives.getStreakInfo();
    if (info.streak <= 0) {
      el.classList.add('hidden');
      return;
    }
    el.classList.remove('hidden');
    el.innerHTML = `${getIconSvg('flame', 12)} ${info.streak}`;
    el.classList.toggle('at-risk', info.atRisk);
    el.classList.toggle('reward', !!info.unseenReward);
    el.title = info.atRisk
      ? `${info.streak}-day streak — claim today's daily jackpot before the reset!`
      : `${info.streak}-day streak`;
  }

  _renderStreak() {
    if (!this.streakEl) return;
    const info = directives.getStreakInfo();
    const unseen = info.unseenReward;
    const rewardLabel = (m) => (m.kind === 'fugazi' ? `${m.amount} FUGAZI` : `${m.amount} PACKS`);
    const rewardIcon = (m) => (m.kind === 'fugazi' ? '⬡' : getIconSvg('crate', 12));

    const markers = info.milestones.map(m => {
      const reached = info.streak >= m.day;
      const justClaimed = !!unseen && unseen.day === m.day && reached;
      const isNext = !reached && info.nextMilestone && m.day === info.nextMilestone.day;
      const cls = ['streak-marker', reached ? 'reached' : '', isNext ? 'next' : '', justClaimed ? 'just-claimed' : ''].join(' ');
      const cycleDay = m.dayInCycle || (((m.day - 1) % 14) + 1);
      const left = (cycleDay / 14) * 100;
      return `<div class="${cls}" style="left: ${left}%;">
        <span class="streak-marker-dot">${rewardIcon(m)}</span>
        <span class="streak-marker-day">${justClaimed ? 'CLAIMED!' : `DAY ${m.day}`}</span>
        <span class="streak-marker-reward">${rewardLabel(m)}</span>
      </div>`;
    }).join('');

    let hint;
    if (info.streak === 0) {
      hint = 'Claim the daily jackpot to start a streak.';
    } else if (info.doneToday) {
      hint = `Streak secured for today. Next reward in ${info.daysToNextMilestone} ${info.daysToNextMilestone === 1 ? 'day' : 'days'}.`;
    } else {
      hint = info.atRisk
        ? "Streak at risk — claim today's daily jackpot before the reset!"
        : "Claim today's daily jackpot to keep your streak alive.";
    }

    this.streakEl.className = `directives-streak${info.atRisk ? ' at-risk' : ''}${info.doneToday ? ' secured' : ''}`;
    this.streakEl.innerHTML = `
      <div class="streak-head">
        <span class="streak-flame">${getIconSvg('flame', 16)}</span>
        <span class="streak-count">${info.streak}</span>
        <span class="streak-label">DAY STREAK</span>
        <span class="streak-best">BEST ${info.best}</span>
        <span class="streak-freeze ${info.freezeAvailable ? 'ready' : 'used'}" title="${info.freezeAvailable ? 'Streak Freeze ready: a single missed day is forgiven once per week' : 'Streak Freeze used this week'}" aria-label="${info.freezeAvailable ? 'Streak Freeze ready' : 'Streak Freeze used'}"><span class="streak-freeze-icon">${getIconSvg('freeze', 14)}</span></span>
      </div>
      <div class="streak-track">
        <div class="streak-track-fill" style="width: ${(info.cyclePosition / 14) * 100}%;"></div>
        ${markers}
      </div>
      <div class="streak-hint">${hint}</div>
    `;
  }

  updateCountdown() {
    if (!this.countdownEl) return;
    const { hours, mins, secs } = directives.getResetCountdown();
    this.countdownEl.innerHTML = `<span class="countdown-digital-seg">${hours}</span><span class="countdown-digital-colon">:</span><span class="countdown-digital-seg">${mins}</span><span class="countdown-digital-colon">:</span><span class="countdown-digital-seg">${secs}</span>`;
  }

  render() {
    if (!this.listEl) return;

    this.updateCountdown();
    this._renderStreak();

    const allDirectives = directives.getDirectives();
    this.listEl.innerHTML = '';

    const tierBadges = {
      1: { label: 'TIER 1 • ROUTINE', color: '#38bdf8', icon: getIconSvg('rank_diamond', 12) },
      2: { label: 'TIER 2 • TACTICAL', color: '#c044ff', icon: getIconSvg('energy', 12) },
      3: { label: 'TIER 3 • APEX', color: '#ffcc00', icon: getIconSvg('crown', 12) }
    };

    allDirectives.forEach(d => {
      const tierInfo = tierBadges[d.tier] || tierBadges[1];
      const isDone = d.completed;
      const pct = Math.min(100, Math.max(0, (d.progress / d.target) * 100));

      const card = document.createElement('div');
      card.className = `directive-card tier-${d.tier} ${isDone ? 'completed' : 'active'}`;

      card.innerHTML = `
        <div class="directive-card-header">
          <span class="directive-tier-tag" style="color: ${tierInfo.color}; border-color: ${tierInfo.color};">
            ${tierInfo.icon} ${tierInfo.label}
          </span>
          <div class="directive-bounty-pills">
            <span class="directive-fugazi-pill">+${d.rewardFugazi} FUGAZI</span>
            <span class="directive-rank-pill">+${d.rewardRankBonus} Rank</span>
          </div>
        </div>
        <div class="directive-card-body">
          <div class="directive-title">${d.title}</div>
          <div class="directive-desc">${d.description}</div>
        </div>
        <div class="directive-card-footer">
          <div class="directive-progress-info">
            <span class="progress-val">${isDone ? 'COMPLETED ✓' : `${d.progress} / ${d.target}`}</span>
          </div>
          <div class="directive-progress-track">
            <div class="directive-progress-fill" style="width: ${pct}%;"></div>
          </div>
        </div>
      `;

      this.listEl.appendChild(card);
    });

    const completedCount = allDirectives.filter(x => x.completed).length;

    // Daily Reroll Button Handling
    if (this.btnReroll) {
      const isUsed = directives.isRerollUsed();
      const isReady = directives.canReroll();

      if (isUsed) {
        this.btnReroll.style.display = 'none';
        this.btnReroll.disabled = true;
        this.btnReroll.className = 'hud-btn btn-daily-reroll exhausted hidden';
        this.btnReroll.classList.add('hidden');
        if (this.btnRerollFill) this.btnRerollFill.style.height = '0%';
      } else if (isReady) {
        this.btnReroll.style.display = '';
        this.btnReroll.disabled = false;
        this.btnReroll.className = 'hud-btn btn-daily-reroll ready';
        this.btnReroll.classList.remove('hidden');
        if (this.btnRerollText) this.btnRerollText.textContent = 'DAILY REROLL';
        if (this.btnRerollFill) this.btnRerollFill.style.height = '100%';
      } else {
        this.btnReroll.style.display = '';
        this.btnReroll.disabled = true;
        this.btnReroll.className = 'hud-btn btn-daily-reroll';
        this.btnReroll.classList.remove('hidden');
        if (this.btnRerollText) this.btnRerollText.textContent = 'DAILY REROLL';
        const fillPct = (completedCount / 3) * 100;
        if (this.btnRerollFill) this.btnRerollFill.style.height = `${fillPct}%`;
      }
    }

    // Jackpot Banner
    if (this.jackpotBannerEl) {
      const isClaimed = directives.isJackpotClaimed();

      this.jackpotBannerEl.className = `directives-jackpot-banner ${isClaimed ? 'claimed' : ''}`.trim();
      this.jackpotBannerEl.innerHTML = `
        <div class="jackpot-icon ${isClaimed ? 'claimed' : 'deactivated'}">${getIconSvg('trophy', 28)}</div>
        <div class="jackpot-info">
          <div class="jackpot-title">DAILY JACKPOT (+${DAILY_JACKPOT_FUGAZI} FUGAZI)</div>
          <div class="jackpot-subtitle">
            ${isClaimed 
              ? "Today's jackpot successfully claimed!" 
              : `Complete all 3 daily quests (${completedCount}/3) for the daily jackpot.`}
          </div>
        </div>
      `;
    }
  }
}
